import { Immutable } from "immer";
import React from "react";
import { PDPChangeRootProductBlock } from "tsi-common-react/src/apps/configurator/PDPChangeRootProductBlock/index";
import { PDPSelectedRootProductConditionalBlock } from "tsi-common-react/src/apps/configurator/PDPSelectedRootProductConditionalBlock/index";
import { ProductReviewsSSR } from "tsi-common-react/src/apps/reviews/ProductReviewsSSR/index";
import { FeaturesBlock } from "tsi-common-react/src/components/FeaturesBlock/index";
import { isoProductID } from "tsi-common-react/src/models/nominals";

import { templates } from "@reactivated";
import type {
    ChangeRootOptionBlock as ChangeRootOptionBlockValue,
    PDPToolbox,
    PLPHeroBlock as PLPHeroBlockValue,
    PLPToolbox,
    ReviewsFullBlock as ReviewsFullBlockValue,
    SelectedRootProductConditionalBlock as RootConditionalBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { PLPHero } from "../../blocks/PLPHero";
import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

// Stearns reviews brand label. Plain "&" (not "&amp;"): this value is passed as
// the JS expression `brand={STEARNS_REVIEWS_BRAND}`, which -- unlike the
// JSX-attribute literal `brand="Stearns &amp; Foster"` in ProductReviewsTemplate.tsx
// that the JSX compiler decodes to "&" -- undergoes no HTML-entity decoding, so
// "&amp;" here would reach the DOM as the literal five characters "&amp;".
const STEARNS_REVIEWS_BRAND = "Stearns & Foster";

/**
 * Shared assembly for the consolidated Stearns PDP (``PDPToolbox``) and PLP
 * (``PLPToolbox``) page bodies (#33591). Both toolboxes have an identical block
 * set (base ``Toolbox`` + ``reviews_full_block`` / ``features_block`` /
 * ``plp_hero`` / ``change_root_option_block`` / ``root_conditional_block``), so
 * a single dispatcher serves both. The hero chrome above the body
 * (``ProductHeroAssemblyTemplate`` + ``{% modular_configurator %}``) is rendered
 * separately and is NOT part of this assembly.
 *
 * Three blocks are page-dependent: the reactivated body-serialization path
 * threads ``request`` but not ``page``, so their server-computed SSR props are
 * threaded via id-keyed side-channels (``reviews`` / ``changeRootSSR`` /
 * ``rootConditionalSSR``, built by the ``get_pdp_reviews_props`` /
 * ``get_pdp_block_ssr`` templatetags) and looked up here by ``block.id``. The
 * toolbox StreamBlock has no ``max_num`` cap on ``reviews_full_block``, so each
 * reviews block carries its own props in ``reviews`` keyed by ``block.id``.
 */
type IProps = Immutable<{
    value: PDPToolbox | PLPToolbox;
    reviews: {
        [k: string]: templates.ProductReviewsTemplate;
    };
    changeRootSSR: {
        [k: string]: templates.ChangeRootProductBlockTemplate;
    };
    rootConditionalSSR: {
        [k: string]: templates.SelectedRootProductConditionalBlockTemplate;
    };
}>;

const ProductPageBody = (props: IProps): React.JSX.Element => {
    const components = {
        ...toolboxComponentsMap,

        // `features_block` -> FeaturesBlock (self-contained `value` dispatch).
        features_block: FeaturesBlock,

        // `plp_hero` -> best-effort dead-block render (see PLPHero docstring:
        // gallery + configurator intentionally omitted as page-dependent hero
        // duplicates; only the self-contained value_props is rendered).
        plp_hero: (block: { value: Immutable<PLPHeroBlockValue> }) => (
            <PLPHero value={block.value} />
        ),

        // `reviews_full_block` -> reproduces the legacy
        // `tsicommon_cms/blocks/reviews-full-block.html`: `.reviews-full-block`
        // wrapper, `#reviews` SSR reviews, and the optional CTA (rendered from
        // the block value: `show_cta` / `cta_text` / `cta.url`). The SSR props
        // are looked up per block by `block.id` from the id-keyed `props.reviews`
        // side-channel (the toolbox StreamBlock has no `max_num` cap, so each
        // reviews block renders with its own products/title/filter data). Absent
        // only when no reviews_full_block exists, so the guard never hides a
        // present block.
        reviews_full_block: (block: {
            id?: string;
            value: Immutable<ReviewsFullBlockValue>;
        }) => {
            const reviews = block.id ? props.reviews[block.id] : undefined;
            return (
                <div className="reviews-full-block">
                    {reviews && (
                        <>
                            <div id="reviews">
                                <ProductReviewsSSR
                                    {...reviews}
                                    brand={STEARNS_REVIEWS_BRAND}
                                    isCollapsedView={true}
                                />
                            </div>
                            {block.value.show_cta && (
                                <div className="pdp-reviews-full__cta">
                                    <a
                                        className="button button--ghost pdp-reviews-full__cta-link"
                                        href={block.value.cta?.url ?? ""}
                                    >
                                        {block.value.cta_text}
                                    </a>
                                </div>
                            )}
                        </>
                    )}
                </div>
            );
        },

        // `change_root_option_block` -> reproduces the legacy
        // `tsicommon_cms/blocks/pdp-change-root-option-block.html` wrapper
        // (`#pdp-change-root-block` + optional `|safe` title) around
        // PDPChangeRootProductBlock. SSR props (root products list + selected
        // id) come from the id-keyed `changeRootSSR` side-channel; `optionInput
        // Type`/`title` come from the block value.
        change_root_option_block: (block: {
            id?: string;
            value: Immutable<ChangeRootOptionBlockValue>;
        }) => {
            const ssr = block.id ? props.changeRootSSR[block.id] : undefined;
            const optionInputType = block.value.optionInputType ?? "";
            return (
                <div
                    tabIndex={0}
                    id="pdp-change-root-block"
                    className={`pdp-change-root-option-block l-capped-width ${optionInputType}`}
                >
                    {block.value.title && (
                        <h2
                            className="pdp-change-variant-option-block__title"
                            dangerouslySetInnerHTML={{
                                __html: block.value.title,
                            }}
                        />
                    )}
                    {ssr && (
                        <PDPChangeRootProductBlock
                            inputType={ssr.input_type}
                            initialRootProductsData={
                                ssr.initial_root_products_data
                            }
                            initialSelectedRootProductId={
                                ssr.initial_selected_root_product_id
                            }
                        />
                    )}
                </div>
            );
        },

        // `root_conditional_block` -> PDPSelectedRootProductConditionalBlock.
        // The legacy `pdp-selected-root-product-conditional-block.html` adds no
        // wrapper (the container emits its own gated tabpanel div), so dispatch
        // directly. The gating props (block product id + initial visibility)
        // come from the id-keyed `rootConditionalSSR` side-channel; the block's
        // `content` (a `PNPToolbox` StreamBlock) is rendered as real React
        // children through this same `components` map (#33285) -- replacing the
        // legacy JSON `content_segments` /`dangerouslySetInnerHTML` path so every
        // nested toolbox block hydrates. `components` is captured by reference
        // below (define-then-close-over), exactly like the recursive
        // `promo_conditional_block` entry in `toolboxComponentsMap`. NOTE: the
        // page-body `components` (not `toolboxComponentsMap`) is dispatched here
        // so that `PNPToolbox`-only blocks (`features_block`, `reviews_full_block`,
        // `plp_hero`) resolve; `PNPToolbox` does not include
        // `change_root_option_block` / `root_conditional_block`, so there is no
        // recursion.
        root_conditional_block: (block: {
            id?: string;
            value: Immutable<RootConditionalBlockValue>;
        }) => {
            const ssr = block.id
                ? props.rootConditionalSSR[block.id]
                : undefined;
            if (!ssr) {
                return null;
            }
            return (
                <PDPSelectedRootProductConditionalBlock
                    blockProductID={isoProductID.wrap(ssr.block_product_id)}
                    isInitiallyVisible={ssr.is_initially_visible}
                >
                    <StreamField
                        tagName={React.Fragment}
                        value={block.value.content ?? []}
                        components={components}
                    />
                </PDPSelectedRootProductConditionalBlock>
            );
        },
    };
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.value}
            components={components}
        />
    );
};

export const ProductDisplayPageBody = ProductPageBody;
export const ProductLinePageBody = ProductPageBody;
